// Need to use the React-specific entry point to import createApi
import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import { Config } from "../config";
import { SearchSessionQueryArgs } from "./types/rtkQueryTypes";
import { EventDocument } from "./types/search.service.types";
import {
    AddAssessmentListRequest,
    SessionSetupAssessmentsData,
    customNotification,
    getAssetDocumentKey,
    NotificationMetadataRequest,
    NotificationRequest,
    NotificationResponse,
    ParticipantEmailTemplate,
    ProgramTemplateList,
    SendParticipantEmailRequest,
    SendParticipantEmailResult,
    UpdateSessionProgramDataRequest,
    SessionDetailType,
    SessionSetupData,
    setupResponse,
    TemplateRequest,
    SessionProgramManager,
    SessionCalendarRequest,
    SessionCalendarEvent,
    AssessmentNotificationStatus,
    SessionLanguage,
    AvailableTransferSession,
    AvailableAssessments,
    CreateSessionRequest,
    UpdateProgramManagerResponse,
    UpdateProductSaleSessionRequest,
} from "./types/sessionDetailTypes";
import {
    SessionEventOption,
    SessionOptions,
    SetSessionEventOptionRequest,
} from "./types/sessionOptionsType";
import {
    AdvancedSearchRequest,
    AdvancedSearchResults,
} from "./types/enterpriseParticipantApiTypes";
import { cclTokenedEnterpriseParticipantApi } from "./cclTokenedEnterpriseParticipantApi";

const apiManagementRootUrl: string | undefined = Config.REACT_APP_CCL_API_MANAGEMENT_URL;

// Define a service using a base URL and expected endpoints
export const cclTokenedSessionApi = createApi({
    reducerPath: "cclTokenedSessionApi",
    tagTypes: [
        "Sessions",
        "SessionDetail",
        "SessionOptions",
        "SessionAssets",
        "SessionSetupAssessments",
        "SessionSetUp",
        "ProgramManagerSessions",
        "SessionSetupProgramManagers",
        "DocumentAiKey",
        "CustomNotification",
        "PreviewNotification",
        "NotificationMetadata",
        "ParticipantEmailTemplates",
    ],
    baseQuery: fetchBaseQuery({
        baseUrl: apiManagementRootUrl,
        prepareHeaders: (headers, { getState }) => {
            //we should probably change this.
            const token = localStorage.getItem("token") as string;
            headers.set("authorization", `Bearer ${token}`);
            return headers;
        },
    }),
    endpoints: (builder) => ({
        getCclEvents: builder.mutation<EventDocument[], Partial<SearchSessionQueryArgs>>({
            query: (args) => ({
                url: `enterprise-session/Sessions/search`,
                method: "POST",
                body: args,
            }),
        }),
        setEventSessionOption: builder.mutation<SessionEventOption, SetSessionEventOptionRequest>({
            query: (args) => ({
                url: `enterprise-session/SessionOptions/${args.eskey}`,
                method: "POST",
                body: args.eventOption,
            }),
            invalidatesTags: ["SessionOptions"],
        }),
        getCclPendingEvents: builder.query<EventDocument[], void>({
            query: () => "enterprise-session/Sessions/pending",
        }),
        getCclEventByEventKey: builder.query<SessionDetailType, string>({
            query: (eskey) => `enterprise-session/Sessions/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "SessionDetail", id: eskey }],
        }),
        getCalendarEvents: builder.query<SessionCalendarEvent[], SessionCalendarRequest>({
            query: (request) =>
                `enterprise-session/Sessions/calendar?start=${request.start}&end=${request.end}`,
        }),
        getSessionSetUpByEventKey: builder.query<SessionSetupData, string>({
            query: (eskey) => `enterprise-session/SessionSetUp/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "SessionSetUp", id: eskey }],
        }),
        getSessionSetUpAssessmentsByEventKey: builder.query<SessionSetupAssessmentsData, string>({
            query: (eskey) => `enterprise-session/SessionSetUp/${eskey}/assessments`,
            providesTags: (_, __, eskey) => [{ type: "SessionSetupAssessments", id: eskey }],
        }),
        getSessionSetUpProgramManagersByEventKey: builder.query<SessionProgramManager[], string>({
            query: (eskey) => `enterprise-session/SessionSetUp/${eskey}/program-managers`,
            providesTags: (_, __, eskey) => [{ type: "SessionSetupProgramManagers", id: eskey }],
        }),
        getNotificationByEventKey: builder.query<customNotification, string>({
            query: (eskey) => `enterprise-session/Notification/${eskey}`,
            providesTags: (_, __, eskey) => [{ type: "CustomNotification", id: eskey }],
        }),
        putProgramManagerSession: builder.mutation<
            UpdateProgramManagerResponse,
            SessionProgramManager
        >({
            query: (ProgramManager) => ({
                url: "enterprise-session/SessionSetUp/UpdateProgramMangager",
                method: "POST",
                body: ProgramManager,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
            invalidatesTags: (_, __, ProgramManager) => [
                { type: "SessionSetupProgramManagers", id: ProgramManager.programId },
            ],
        }),
        previewNotification: builder.mutation<NotificationResponse, NotificationRequest>({
            query: (NotificationRequest) => ({
                url: "enterprise-session/Notification/PreviewNotificationMetaData",
                method: "PUT",
                body: NotificationRequest,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
                providesTags: (_: any, __: any, request: NotificationRequest) => [
                    { type: "PreviewNotification", id: request.eskey },
                ],
            }),
        }),
        getSessionOptionsByEventKey: builder.query<SessionOptions, string>({
            query: (eskey) => `enterprise-session/SessionOptions/${eskey}`,
            providesTags: ["SessionOptions"],
        }),
        getNotificationMetadata: builder.mutation<
            NotificationResponse,
            NotificationMetadataRequest
        >({
            query: (NotificationMetadataRequest) => ({
                url: "enterprise-session/Notification/GetNotificationData",
                method: "POST",
                body: NotificationMetadataRequest,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
                providesTags: (_: any, __: any, request: NotificationRequest) => [
                    { type: "NotificationMetadata", id: request.eskey },
                ],
            }),
        }),
        resetNotificationMetadata: builder.mutation<
            NotificationResponse,
            NotificationMetadataRequest
        >({
            query: (NotificationMetadataRequest) => ({
                url: "enterprise-session/Notification/NotificationMetaDataReset",
                method: "POST",
                body: NotificationMetadataRequest,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
            invalidatesTags: (_: any, __: any, request: NotificationMetadataRequest) => [
                { type: "NotificationMetadata", id: request.eskey },
                { type: "CustomNotification", id: request.eskey },
                { type: "PreviewNotification", id: request.eskey },
            ],
        }),
        saveNotificationMetadata: builder.mutation<NotificationResponse, NotificationRequest>({
            query: (NotificationMetadataRequest) => ({
                url: "enterprise-session/Notification/SaveNotificationMetaData",
                method: "PUT",
                body: NotificationMetadataRequest,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
            invalidatesTags: (_: any, __: any, request: NotificationMetadataRequest) => [
                { type: "NotificationMetadata", id: request.eskey },
                { type: "CustomNotification", id: request.eskey },
                { type: "PreviewNotification", id: request.eskey },
            ],
        }),
        getProgramTemplatebyTemplateId: builder.mutation<ProgramTemplateList, TemplateRequest>({
            query: (arg) => ({
                url: `enterprise-session/SessionSetUp/GetTemplateData/${arg.eskey}/${arg.programTemplateId}`,
                method: "POST",
                body: arg,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
        }),
        getAIKeybyDocKey: builder.query<getAssetDocumentKey, number>({
            query: (docKey) => `enterprise-session/SessionSetUp/GetDocumentAssetKey/${docKey}`,
            providesTags: (_, __, docKey) => [{ type: "DocumentAiKey", id: docKey }],
        }),
        GetValidUSPS: builder.mutation<setupResponse, string | number>({
            query: (USPSId) => ({
                url: `enterprise-session/SessionSetUp/CornerStoneId/${USPSId}`,
                method: "POST",
                body: USPSId,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
        }),
        saveAssessmentList: builder.mutation<setupResponse, AddAssessmentListRequest>({
            query: (request) => ({
                url: `enterprise-session/SessionSetUp/Add_Assessment/${request.esKey}`,
                method: "POST",
                body: request.assessmentList,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
            invalidatesTags: (_, __, request) => [
                { type: "SessionSetupAssessments", id: request.esKey },
            ],
        }),
        putSessionSetUp: builder.mutation<setupResponse, UpdateSessionProgramDataRequest>({
            query: (request) => ({
                url: "enterprise-session/SessionSetUp/SessionSetUpDto",
                method: "POST",
                body: request,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
            invalidatesTags: (_, __, request) => [
                { type: "SessionSetUp", id: request.programId },
                { type: "SessionDetail", id: request.programId },
            ],
            async onQueryStarted(request, { dispatch, queryFulfilled }) {
                await queryFulfilled;
                dispatch(
                    cclTokenedEnterpriseParticipantApi.util.invalidateTags([
                        { type: "Participants", id: request.programId },
                    ]),
                );
            },
        }),
        updateSessionCoachesAccess: builder.query<void, string>({
            query: (eskey) => `enterprise-coach-assignment/v1.0/eventcoaches/${eskey}/checkassets`,
        }),
        getProgramManagersSessions: builder.query<EventDocument[], string>({
            query: (imkey) => `enterprise-session/Sessions/by-manager/${imkey}`,
            providesTags: (_, __, imkey) => [{ type: "ProgramManagerSessions", id: imkey }],
        }),
        updateAssessmentNotificationStatus: builder.mutation<void, AssessmentNotificationStatus>({
            query: (payload) => ({
                url: `enterprise-session/SessionSetUp/UpdateAssessmentNotificationStatus/${payload.eskey}`,
                method: "POST",
                body: payload.payload,
            }),
        }),
        getSessionOptions: builder.query<any, void>({
            query: () => `enterprise-session/AdvancedSearch/advanced-search-options`,
            providesTags: ["SessionOptions"],
        }),
        runAdvancedSessionSearch: builder.mutation<AdvancedSearchResults, AdvancedSearchRequest>({
            query: (body) => ({
                url: `enterprise-session/AdvancedSearch/advanced-search`,
                method: "POST",
                body: body,
            }),
        }),
        getAvailableAssesments: builder.query<AvailableAssessments[], void>({
            query: () => `enterprise-session/ProductSales/available-assessments`,
        }),
        createNewSession: builder.mutation<SessionDetailType, CreateSessionRequest>({
            query: (body) => ({
                url: `enterprise-session/ProductSales`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: ["ProgramManagerSessions"],
        }),
        getAvailableLanguages: builder.query<SessionLanguage[], string>({
            query: (esiKey) => `enterprise-session/ProductSales/available-languages/${esiKey}`,
        }),
        getUserSessionList: builder.query<AvailableTransferSession[], string>({
            query: (esKey) => `enterprise-session/ProductSales/transfer-options/${esKey}`,
        }),
        getPartnerSessionList: builder.query<AvailableTransferSession[], string>({
            query: (esKey) => `enterprise-session/Sessions/transfer-options/${esKey}`,
        }),
        updateProductSaleSession: builder.mutation<void, UpdateProductSaleSessionRequest>({
            query: ({ esKey, ...body }) => ({
                url: `enterprise-session/ProductSales/update/${esKey}`,
                method: "PUT",
                body: body,
            }),
            invalidatesTags: (_, __, request) => [
                { type: "SessionDetail", id: request.esKey.toString() },
            ],
        }),
        getParticipantEmailTemplates: builder.query<
            ParticipantEmailTemplate[],
            { esKey: string; languageKey?: number }
        >({
            // Delivery mode (Live Online vs In Person) is intentionally not passed here —
            // the API derives it from the session's own SessionFormat when isLiveOnline is
            // omitted, so the UI doesn't need to know how that determination is made.
            query: ({ esKey, languageKey }) =>
                `enterprise-session/Notification/${esKey}/participants/email/templates${
                    languageKey ? `?languageKey=${languageKey}` : ""
                }`,
            providesTags: (_, __, { esKey }) => [{ type: "ParticipantEmailTemplates", id: esKey }],
        }),
        sendParticipantEmail: builder.mutation<
            SendParticipantEmailResult,
            { esKey: string; request: SendParticipantEmailRequest }
        >({
            query: ({ esKey, request }) => ({
                url: `enterprise-session/Notification/${esKey}/participants/email`,
                method: "POST",
                body: request,
                headers: {
                    "Content-type": "application/json; charset=UTF-8",
                },
            }),
        }),
    }),
});

// Export hooks for usage in functional components, which are
// auto-generated based on the defined endpoints
export const {
    useGetCclEventsMutation,
    useSetEventSessionOptionMutation,
    useGetCclPendingEventsQuery,
    useGetCclEventByEventKeyQuery,
    useLazyGetCalendarEventsQuery,
    useGetSessionSetUpByEventKeyQuery,
    useGetSessionSetUpAssessmentsByEventKeyQuery,
    useGetSessionSetUpProgramManagersByEventKeyQuery,
    useGetNotificationByEventKeyQuery,
    useGetSessionOptionsByEventKeyQuery,
    usePreviewNotificationMutation,
    useGetNotificationMetadataMutation,
    usePutProgramManagerSessionMutation,
    useResetNotificationMetadataMutation,
    useSaveNotificationMetadataMutation,
    useSaveAssessmentListMutation,
    usePutSessionSetUpMutation,
    useLazyGetAIKeybyDocKeyQuery,
    useGetValidUSPSMutation,
    useUpdateSessionCoachesAccessQuery,
    useGetProgramManagersSessionsQuery,
    useUpdateAssessmentNotificationStatusMutation,
    useGetSessionOptionsQuery,
    useRunAdvancedSessionSearchMutation,
    useGetAvailableAssesmentsQuery,
    useCreateNewSessionMutation,
    useGetAvailableLanguagesQuery,
    useGetUserSessionListQuery,
    useGetPartnerSessionListQuery,
    useUpdateProductSaleSessionMutation,
    useGetParticipantEmailTemplatesQuery,
    useSendParticipantEmailMutation,
} = cclTokenedSessionApi;
